import { BaseWebComponentDataProvider } from '../../framework/BaseWebComponentDataProvider';
import { sanitizeObject } from '../../utils/sanitization.utils';
import { CardMessage, HomePageSettings } from '../types';
import { isEmpty } from 'lodash';
import Target from '@repobit/dex-target';

export class GzLandingPageAppDataProvider extends BaseWebComponentDataProvider {
  public target: Target;

  public async getGzVersion(): Promise<string | undefined> {
    try {
      const res = await this.dependencies.api.clients.GzData.getGzVersion().toPromise();
      return sanitizeObject(res);
    } catch (e) {
      // do not display the version if there is an error.
      return '';
    }
  }

  public async getCardMessages(): Promise<CardMessage[] | undefined> {
    try {
      const res = await this.dependencies.api.clients.CardMessages.getCardMessages().toPromise();
      return sanitizeObject(res);
    } catch (e) {
      return [];
    }
  }

  public async getRssFeedUrls(companyId: string | undefined): Promise<string[] | undefined> {
    const fallbackUrls = [
      'https://www.bitdefender.com/nuxt/api/en-us/rss/businessinsights',
      'https://www.bitdefender.com/nuxt/api/en-us/rss/labs/',
    ];
    try {
      const res = (await this.dependencies.api.clients.RssFeedUrl.getRssFeedUrls(
        companyId
      ).toPromise()) as HomePageSettings;
      const urls = sanitizeObject(res.newsFeedRssUrls);
      return urls.length > 0 ? urls : fallbackUrls;
    } catch (e) {
      return fallbackUrls;
    }
  }

  public async getDynamicEventWidgetInfo(companyId: string | undefined): Promise<WidgetInfo> {
    try {
      const eventOffer = (await this.target.getOffers({
        mboxNames: 'msp-widget-event-mbox',
        parameters: { companyId: companyId as string },
      })) as Promise<WidgetInfo>;
      if (isEmpty(eventOffer)) {
        throw new Error('Missing required targeted content fields in eventOffers');
      }
      return eventOffer;
    } catch (error) {
      return Promise.resolve({
        title: 'MSP Events and Webinars',
        description:
          'Keep track of our event calendar to discover opportunities to connect with us and learn how our solutions safeguard your customers and help growing your revenue.',
        buttonName: 'View Calendar',
        buttonLink:
          'https://businessresources.bitdefender.com/msp-events-webinars-calendar' +
          '?icid=button|b|landing-page-for-msp|event-default',
        image: 'https://www.bitdefender.com/content/dam/bitdefender/business/msp/Calendar.jpg',
      } as WidgetInfo);
    }
  }

  public async getDynamicPromoWidgetInfo(companyId: string | undefined): Promise<WidgetInfo> {
    try {
      const eventOffer = (await this.target.getOffers({
        mboxNames: 'msp-widget-promo-mbox',
        parameters: { companyId: companyId as string },
      })) as Promise<WidgetInfo>;
      if (isEmpty(eventOffer)) {
        throw new Error('Missing required targeted content fields in promoOffers');
      }
      return eventOffer;
    } catch (error) {
      return Promise.resolve({
        title: 'Bitdefender MDR for MSPs',
        description:
          'Real-time threat detection, award-winning endpoint protection, around-the-clock security monitoring by certified experts. Protect your customers effectively while reducing resource strain.',
        buttonName: 'Read More',
        buttonLink:
          'https://www.bitdefender.com/business/explore/mdr-for-msp/products-mdr-for-msp' +
          '?icid=button|b|landing-page-for-msp|promo-default',
        image: 'https://www.bitdefender.com/content/dam/bitdefender/business/msp/bitdefender-mdr-soc-msp.png',
      } as WidgetInfo);
    }
  }
}
